<template>
  <sp-menu
    class="provider-filter"
    location="bottom-left"
    close-on-content-click="false"
    :open="menuOpen"
    @update-open="handleMenuUpdateOpen"
  >
    <FilterActivatorSlot
      :label="label"
      :text="activatorTitle"
      :prepend-icon="prependIcon"
      :open="menuOpen"
      @clear="clearAll"
    >
      <template v-if="$slots.activator" #activator="activatorProps">
        <slot name="activator" v-bind="activatorProps" />
      </template>
    </FilterActivatorSlot>

    <sp-card class="provider-filter__panel" variant="new-default">
      <sp-card-content>
        <sp-radio-group
          v-if="newPatientItems.length"
          v-model="newPatientRadioModel"
          class="provider-filter__field"
          :label="newPatientLabel"
          name="new_patient"
        >
          <sp-radio
            v-for="option in newPatientItems"
            :key="option.value"
            :value="toRadioValue(option.value)"
            :label="option.label"
          ></sp-radio>
        </sp-radio-group>

        <div class="provider-filter__field">
          <sp-label block>{{ nameLabel }}</sp-label>
          <sp-search-box
            v-model="inputText"
            class="provider-filter__input"
            density="compact"
            clearable
            :input-test-id="TestId.finder.providerFilterInput"
            :placeholder="namePlaceholder"
            @text-input="handleNameTextInput"
            @click-append="handleNameClickAppend"
            @focusout="handleNameFocusOut"
          ></sp-search-box>
          <!--
            Suggestions render inline (no-menu) directly under the field rather
            than in a floating popup, so the Provider Type section below stays
            visible and usable while the list is open. The list only appears
            while the user is actively typing (comboboxOpen) and there are
            matches; an emptied field hides it. Height is capped so a long list
            scrolls instead of stretching the panel.
          -->
          <sp-quantum-select
            v-if="showSuggestions"
            :aria-label="label"
            class="provider-filter__suggestions"
            no-menu
            navigation="accordion"
            start-on-root
            hide-title-bar
            hide-search-bar
            :max-results="PROVIDER_RESULTS_LIMIT"
            :item-test-id="TestId.finder.providerFilterSuggestion"
            max-height="10rem"
            :listen-on-key-event="comboboxOpen"
            :items.prop="models"
            :search="searchQuery"
            @update-selected-item="handleSuggestionUpdateSelectedItem"
          ></sp-quantum-select>
        </div>

        <!--
          `.attr` on both selects, never a bare `:value`: the attribute is sp-select's programmatic
          set channel, so a clear or a URL restore lands — the property channel cannot express a
          clear at all, since Vue coerces a null binding to "" or 0 to match the property's current
          type. A bare binding is also the one form Vue re-applies on every render, writing the
          element's live selection.
        -->
        <sp-select
          class="provider-filter__field"
          :label="providerTypeLabel"
          :items.prop="providerTypeItems"
          :value.attr="providerType"
          :prompt="providerTypePrompt"
          @input="handleProviderTypeInput"
        ></sp-select>

        <template v-if="affiliationEnabled">
          <div v-if="affiliationSingle" class="provider-filter__field">
            <sp-switch
              :checked="affiliationChecked"
              :label="affiliationSingleName"
              @update-checked="handleAffiliationUpdateChecked"
            ></sp-switch>
          </div>
          <sp-select
            v-else
            class="provider-filter__field"
            :label="affiliationLabel"
            :items.prop="affiliationOptions"
            :value.attr="selectedAffiliation"
            :prompt="affiliationPrompt"
            @input="handleAffiliationInput"
          ></sp-select>
        </template>
      </sp-card-content>
    </sp-card>
  </sp-menu>
</template>

<script setup>
import { computed, watch } from "vue";

import { booleanAttribute, emptyToUndefined, toJSON, trimToUndefined } from "#ce/utils/props";
import { firstDetail } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { useControllableOpen } from "#ce/shared/filters/composables/useControllableOpen";
import { useQuantumSearchField } from "#ce/shared/filters/composables/useQuantumSearchField";
import {
  PROVIDER_RESULTS_LIMIT,
  providerSelection,
  useProviderFilter,
  useProviderFilterModel,
} from "#ce/shared/filters/provider/composables/useProviderFilter";
import { useProviderFilterSummary } from "#ce/shared/filters/provider/composables/useProviderFilterSummary";
import FilterActivatorSlot from "#ce/shared/filters/components/FilterActivatorSlot.ce.vue";

const emit = defineEmits(["update:model-value", "update:open"]);

const props = defineProps({
  /**
   * Clear signal from the SP Filter wrapper's v-model. A falsy value resets every
   * sub-selection (chip clear / deactivate); non-empty echoes are ignored —
   * restoring values flows through `setFilterValues`.
   */
  modelValue: {
    type: [Object, String],
    default: undefined,
  },
  /**
   * The doctor-finder embed uuid → selects the shared omnisearch store instance
   * that backs the name combobox.
   */
  uuid: {
    type: String,
    default: undefined,
  },
  /** @type {Boolean|String} */
  open: booleanAttribute(),
  /** Chip label shown by the filter-bar activator. */
  label: {
    type: String,
    default: "Provider",
  },
  /** Icon shown on the filter-bar activator chip. */
  prependIcon: {
    type: String,
    default: "doctor-filled",
  },
  /** Connective joining the chip summary's name clause ("Any provider named Smith"). */
  namedLabel: {
    type: String,
    default: "named",
  },
  /** Generic provider noun swapped out for the chosen provider type in the chip summary. */
  providerLabel: {
    type: String,
    default: "provider",
  },
  newPatientLabel: {
    type: String,
    default: "Accepting",
  },
  // Array, or a JSON string when set as a DOM attribute through sp-finder-filter
  // (which forwards it to this child verbatim — only a real custom element would
  // JSON-parse an Array-typed attribute).
  newPatientOptions: {
    type: [Array, String],
    default: () => [
      { value: "true", label: "New patients" },
      { value: "false", label: "Returning patients" },
      { value: "", label: "Any" },
    ],
  },
  nameLabel: {
    type: String,
    default: "Name",
  },
  namePlaceholder: {
    type: String,
    default: "",
  },
  providerTypeLabel: {
    type: String,
    default: "Provider type",
  },
  providerTypeOptions: {
    type: [Array, String],
    default: () => [],
  },
  providerTypePlaceholder: {
    type: String,
    default: undefined,
  },
  /**
   * Affiliation sub-control config (conditional per publishing domain):
   * `{ enabled, mode: 'single' | 'multi', include_in_provider_chip, options: [{title, value}], single: {id, name} }`.
   */
  affiliation: {
    type: [Object, String],
    default: () => ({ enabled: false }),
  },
  affiliationLabel: {
    type: String,
    default: "Affiliation",
  },
  affiliationPlaceholder: {
    type: String,
    default: undefined,
  },
});

const {
  newPatient,
  name: nameValue,
  providerType,
  affiliation: selectedAffiliation,
  model,
  filterNames: ownedFilterNames,
  setFilterValues,
  clear,
} = useProviderFilterModel();

// The standalone `accepting_new_patients` filter, when enabled, owns the
// new_patient field — the server then withholds this control's new-patient-options
// (see _provider.html.erb / _accepting_new_patients.html.erb). Drop new_patient from
// the params we advertise so the bridge doesn't clear the standalone filter's
// selection on every provider change (it zeroes every owned param before re-applying).
const filterNames = computed(() => {
  const ownsNewPatient = (toJSON(props.newPatientOptions, true) ?? []).length > 0;
  return ownsNewPatient ? ownedFilterNames : ownedFilterNames.filter((filterName) => filterName !== "new_patient");
});

// --- filter-bar menu (chip activator + dropdown panel) ---
const menuOpen = useControllableOpen(() => props.open, emit);

function handleMenuUpdateOpen(event) {
  const isOpen = Boolean(firstDetail(event));
  menuOpen.value = isOpen;

  // Reopening shows just the controls; the suggestion list only returns once the
  // user types again (a restored name shouldn't reopen the list).
  if (isOpen) {
    comboboxOpen.value = false;
  }
}

function clearAll() {
  clear();
  setText("");
}

// --- name combobox (shared omnisearch store, free-text + provider navigation) ---
const {
  inputText,
  searchQuery,
  open: comboboxOpen,
  applyInput,
  applyClear,
  setText,
} = useQuantumSearchField({ closeWhenEmpty: true });

const { models } = useProviderFilter({ uuid: trimToUndefined(props.uuid), searchQuery });

// The inline suggestion list is shown only while the user is actively typing
// (comboboxOpen) and there are matches — an emptied field or a restored name
// keeps it hidden, leaving the Provider Type section directly below the input.
const showSuggestions = computed(() => comboboxOpen.value && models.value.length > 0);

let justSelected = false;

function handleNameTextInput(event) {
  applyInput(event);
  justSelected = false;
}

function handleNameClickAppend() {
  comboboxOpen.value = false;
  applyClear();
  nameValue.value = undefined;
}

// Blurring the name input commits the typed text as the `name` filter (clicking
// a radio, Provider Type, or outside the field all blur it). Selecting a provider
// navigates instead, so the just-selected guard skips the commit.
function handleNameFocusOut() {
  if (justSelected) {
    justSelected = false;
    return;
  }
  nameValue.value = trimToUndefined(inputText.value);
}

function handleSuggestionUpdateSelectedItem(event) {
  const item = firstDetail(event);
  if (!item) {
    return;
  }

  justSelected = true;
  const selection = providerSelection(item);

  if (selection.type === "navigate") {
    // Selecting a specific provider is terminal — leave the results page for
    // their profile.
    window.location.assign(selection.url);
    return;
  }

  setText(selection.value);
  nameValue.value = trimToUndefined(selection.value);
}

// Keep the field text in sync when `name` is restored externally (deep-link, chip clear).
function syncFieldTextFromName(value) {
  if ((value ?? "") !== inputText.value) {
    setText(value ?? "");
  }
}

watch(nameValue, syncFieldTextFromName);

// --- provider type + affiliation ---
function handleProviderTypeInput(event) {
  providerType.value = emptyToUndefined(firstDetail(event));
}

// Normalize props that may arrive as JSON strings (DOM attribute) or as real
// arrays/objects (Storybook / programmatic use).
const newPatientItems = computed(() => toJSON(props.newPatientOptions, true) ?? []);

// sp-radio-group reserves "" for "no selection", so the Any option's configured "" value
// crosses to the radios as this sentinel. The filter model keeps "" = Any (param omitted);
// only the radio boundary speaks the sentinel.
const ANY_RADIO_VALUE = "__any__";

function toRadioValue(value) {
  return value === "" ? ANY_RADIO_VALUE : value;
}

const newPatientRadioModel = computed({
  get: () => toRadioValue(newPatient.value),
  set(value) {
    newPatient.value = value === ANY_RADIO_VALUE ? "" : value;
  },
});
const providerTypeItems = computed(() => toJSON(props.providerTypeOptions, true) ?? []);
const affiliationConfig = computed(() => toJSON(props.affiliation, true) ?? {});

const affiliationEnabled = computed(() => Boolean(affiliationConfig.value.enabled));
const affiliationSingle = computed(() => affiliationConfig.value.mode === "single");
const affiliationSingleName = computed(() => affiliationConfig.value.single?.name);
const affiliationOptions = computed(() => affiliationConfig.value.options ?? []);
const affiliationChecked = computed(() => Boolean(selectedAffiliation.value));
const providerChipAffiliation = computed(() =>
  affiliationConfig.value.include_in_provider_chip === false ? undefined : selectedAffiliation.value,
);

const providerTypePrompt = computed(() => props.providerTypePlaceholder || "All Types");
const affiliationPrompt = computed(() => props.affiliationPlaceholder || "Any affiliation");

// The read-only chip summary sentence ("A new Physician (MD) named Smith") is
// derived in a composable so the sentence logic is unit-testable without mounting.
const { activatorTitle, hasActiveSelection } = useProviderFilterSummary({
  newPatient,
  name: nameValue,
  providerType,
  affiliation: providerChipAffiliation,
  newPatientItems,
  providerTypeItems,
  affiliationSingle,
  affiliationSingleName,
  affiliationOptions,
  namedLabel: () => props.namedLabel,
  providerLabel: () => props.providerLabel,
});

defineExpose({ props, filterNames, setFilterValues, clear, active: hasActiveSelection });

function handleAffiliationInput(event) {
  selectedAffiliation.value = emptyToUndefined(firstDetail(event));
}

function handleAffiliationUpdateChecked(event) {
  const checked = Boolean(firstDetail(event));
  const id = affiliationConfig.value.single?.id;
  selectedAffiliation.value = checked && id != null ? String(id) : undefined;
}

// --- model emit + clear signal ---
function emitModelValue(value) {
  emit("update:model-value", value);
}

// A falsy modelValue echo is the wrapper's clear/deactivate signal.
function clearOnModelValueReset(value) {
  if (!value) {
    clear();
  }
}

watch(model, emitModelValue);
watch(() => props.modelValue, clearOnModelValueReset);
</script>

<style>
:host {
  display: block;
  box-sizing: border-box;
  width: 100%;
}
</style>

<style lang="scss" scoped>
// Match the desktop popup width of the other filter-bar menus (DistanceFilter
// sets the same shared token). Without it the menu popup sizes to its content
// (~288px), which crowds the Provider Type select dropdown and clips long
// options like "Osteopathic Physician (DO)".
.provider-filter {
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 20rem);

  sp-select {
    --sp-comp-select-caret-size: var(--sp-comp-provider-filter-select-caret-size, inherit);
    --sp-comp-select-append-padding-inline-end: var(
      --sp-comp-provider-filter-select-append-padding-inline-end,
      inherit
    );
  }
}

.provider-filter__panel {
  min-width: var(--sp-comp-provider-filter-panel-min-width, 18rem);
}

// Padding is owned by the sp-card `new-default` variant. The fields are slotted
// into sp-card-content, so a flex `gap` on the host element can't lay them out —
// space adjacent sections with a margin on the fields themselves instead.
.provider-filter__field + .provider-filter__field {
  margin-top: var(--sp-comp-provider-filter-field-gap, var(--sp-ref-scale-24)); // 24px between sections
}

sp-radio-group {
  --sp-comp-radio-group-items-gap: var(--sp-ref-scale-12); // 12px between radios
}

.provider-filter__field,
.provider-filter__suggestions,
.provider-filter__input {
  width: 100%;
}

// The inline suggestion list sits just under the name input; a small gap keeps
// it visually attached to the field while separating it from the input border.
.provider-filter__suggestions {
  display: block;
  margin-top: var(--sp-ref-scale-6, 0.5rem);
  // sp-quantum-select forces a tall popup min-height (25rem) meant for the
  // floating menu; collapse it so the inline list sizes to its content (capped
  // by `max-height`, scrolling beyond) and the Provider Type section below
  // stays in view instead of being pushed off-screen.
  --sp-comp-quantum-select-popup-min-height: 0;
  // Tighten the list-item line-height so each doctor's name sits close to its
  // grey specialty subtitle.
  --sp-comp-list-item-line-height: 1.4;
}

sp-label {
  margin-bottom: var(--sp-comp-provider-filter-label-margin-bottom, var(--sp-ref-scale-6, 0.5rem));
}
</style>
